import { useState, useEffect } from 'react'
import Logo from '../components/Logo'
import { useLanguage } from '../context/LanguageContext'
import { useModal } from '../context/ModalContext'
import { useIsDark } from '../hooks/useIsDark'
import { t } from '../i18n'
import { scrollTo } from '../utils/scrollTo'
import { CONTACT } from '../constants/contact'
import LanguageSwitcher from '../components/LanguageSwitcher'
import ThemeSwitcher from '../components/ThemeSwitcher'
import VantechLogo from '../assets/svg-components/VantechLogo'

const Header = () => {
	const { language } = useLanguage()
	const { isContactModalOpen, setIsContactModalOpen } = useModal()
	const isDark = useIsDark()
	const [isOpen, setIsOpen] = useState(false)
	const [isScrolled, setIsScrolled] = useState(false)

	const toggleMenu = () => {
		setIsOpen(!isOpen)
	}

	const toggleContactModal = () => {
		setIsContactModalOpen(!isContactModalOpen)
	}

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 72)
		}

		handleScroll()
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isOpen])

	const handleEmailClick = () => {
		window.location.href = `mailto:${CONTACT.email}`
	}

	const handleWhatsAppClick = () => {
		window.open(CONTACT.whatsapp, '_blank')
	}

	return (
		<>
			{isOpen && (
				<div className={`fixed inset-0 bg-opacity-80 backdrop-blur-md z-[105] md:hidden transition-colors duration-300 ${isDark ? 'bg-slate-800' : 'bg-white'}`} />
			)}

			{isContactModalOpen && (
				<div
					className={`fixed inset-0 bg-opacity-80 backdrop-blur-md z-[110] flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-slate-800' : 'bg-white'}`}
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							toggleContactModal()
						}
					}}
				>
					<div className={`p-8 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors duration-300 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
						<div className='flex justify-between items-center mb-6'>
							<h2 className={`text-2xl font-bold transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-900'}`}>
								{t(language, 'contactModal.title')}
							</h2>
							<button
								onClick={toggleContactModal}
								className={`transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-slate-900'}`}
							>
								<svg
									className='w-6 h-6'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										d='M6 18L18 6M6 6l12 12'
									/>
								</svg>
							</button>
						</div>
						<div className='flex flex-col gap-4'>
							<button
								onClick={handleEmailClick}
								className='flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
							>
								<svg
									className='w-6 h-6'
									fill='currentColor'
									viewBox='0 0 20 20'
								>
									<path d='M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z' />
									<path d='M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' />
								</svg>
								<span>{t(language, 'contactModal.sendEmail')}</span>
							</button>
							<button
								onClick={handleWhatsAppClick}
								className='flex items-center gap-3 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors'
							>
								<svg
									className='w-6 h-6'
									fill='currentColor'
									viewBox='0 0 24 24'
								>
									<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.223-.298.335-.447.112-.149.056-.279-.008-.391-.064-.111-.298-.272-.298-.272s-.545-.41-.747-.561c-.2-.15-.43-.26-.615-.26-.173 0-.371.074-.57.223-.198.149-.76.74-.76 1.81s.78 2.1.888 2.243c.108.144 1.533 2.457 3.74 3.44.518.231.924.369 1.238.473.518.15 1.001.128 1.377.078.409-.054 1.258-.506 1.436-.992.178-.486.178-.902.124-.992-.053-.089-.198-.149-.446-.248z' />
								</svg>
							<span>{t(language, 'contactModal.chatWhatsApp')}</span>
						</button>
						<a
							href={CONTACT.vantechUrl}
							target='_blank'
							rel='noopener noreferrer'
							className='flex items-center gap-3 px-4 py-3 bg-[#0070FF] hover:bg-[#0059cc] text-white rounded-lg transition-colors'
						>
							<VantechLogo className='w-6 h-6' />
							<span>{t(language, 'contactModal.visitVantech')}</span>
						</a>
					</div>
					</div>
				</div>
			)}

			<header
				className={`fixed left-0 right-0 z-[100] isolate pointer-events-none transition-all duration-500 ease-out ${
					isScrolled ? 'top-3 px-3 md:top-4 md:px-6' : 'top-0 px-0'
				} ${isOpen ? 'opacity-0' : 'opacity-100'}`}
			>
				<div
					className={`pointer-events-auto mx-auto flex justify-between items-center transition-all duration-1000 ease-out ${
						isScrolled
							? 'max-w-7xl rounded-2xl px-4 py-2 shadow-2xl backdrop-blur-lg md:max-w-7xl md:px-6 md:py-2.5'
							: 'w-full max-w-none rounded-none px-4 py-0 shadow-lg backdrop-blur-md'
					} ${isOpen ? 'pointer-events-none' : ''} ${
						isDark
							? `text-white ${isScrolled ? 'bg-black/70' : 'bg-black/40'}`
							: `text-slate-900 ${isScrolled ? 'bg-white/70' : 'bg-white/40'}`
					}`}
				>
					<a
						href='#home'
						onClick={(e) => { e.preventDefault(); scrollTo('home') }}
						className='cursor-pointer shrink-0'
					>
					<Logo
						width='120'
						height='80'
						className={`w-auto transition-all duration-500 ease-out ${
							isScrolled ? 'h-11 md:h-14' : 'h-20 md:h-24'
						}`}
					/>
					</a>
					<div className='flex items-center gap-1 md:hidden'>
						<ThemeSwitcher />
						<LanguageSwitcher />
						<button
							onClick={toggleMenu}
							className={`focus:outline-none transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-900'}`}
						>
							<svg
								className={`transition-all duration-500 ${
									isScrolled ? 'h-5 w-5' : 'h-6 w-6'
								}`}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='2'
									d='M4 6h16M4 12h16m-7 6h7'
								/>
							</svg>
						</button>
					</div>

					<nav
						className={`hidden items-center md:flex ${
							isScrolled ? 'gap-3 text-sm' : 'gap-6'
						}`}
					>
						<a
							href='#about'
							onClick={(e) => { e.preventDefault(); scrollTo('about') }}
							className={`cursor-pointer transition-colors duration-300 ${isDark ? 'hover:text-gray-400' : 'hover:text-slate-500'}`}
						>
							{t(language, 'nav.about')}
						</a>
						<a
							href='#work'
							onClick={(e) => { e.preventDefault(); scrollTo('work') }}
							className={`cursor-pointer transition-colors duration-300 ${isDark ? 'hover:text-gray-400' : 'hover:text-slate-500'}`}
						>
							{t(language, 'nav.work')}
						</a>
						<ThemeSwitcher />
						<LanguageSwitcher />
						<button
							onClick={toggleContactModal}
							className={`rounded-lg transition-all duration-500 ${
								isDark
									? 'bg-slate-800 text-white hover:bg-blue-800'
									: 'bg-slate-900 text-white hover:bg-slate-700'
							} ${isScrolled ? 'px-4 py-1.5 text-sm' : 'px-6 py-2'}`}
						>
							{t(language, 'nav.contact')}
						</button>
					</nav>
				</div>
			</header>

			<div
				className={`fixed inset-0 transform ${
					isOpen ? 'translate-x-0' : 'translate-x-full'
				} transition-transform duration-300 ease-in-out md:hidden z-[105] ${
					isDark ? 'bg-slate-900' : 'bg-white'
				}`}
			>
				<button
					onClick={toggleMenu}
					className={`absolute top-8 right-8 transition-colors duration-300 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
				>
					<svg
						className='w-8 h-8'
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth='2'
							d='M6 18L18 6M6 6l12 12'
						/>
					</svg>
				</button>
				<div className='h-full flex flex-col justify-center items-center'>
					<nav className='space-y-8'>
						<a
							href='#home'
							onClick={(e) => { e.preventDefault(); scrollTo('home'); toggleMenu() }}
							className={`text-2xl cursor-pointer block transition-colors duration-300 ${isDark ? 'text-slate-300 hover:text-gray-400' : 'text-slate-600 hover:text-slate-900'}`}
						>
							{t(language, 'nav.home')}
						</a>
						<a
							href='#about'
							onClick={(e) => { e.preventDefault(); scrollTo('about'); toggleMenu() }}
							className={`text-2xl cursor-pointer block transition-colors duration-300 ${isDark ? 'text-slate-300 hover:text-gray-400' : 'text-slate-600 hover:text-slate-900'}`}
						>
							{t(language, 'nav.about')}
						</a>
						<a
							href='#work'
							onClick={(e) => { e.preventDefault(); scrollTo('work'); toggleMenu() }}
							className={`text-2xl cursor-pointer block transition-colors duration-300 ${isDark ? 'text-slate-300 hover:text-gray-400' : 'text-slate-600 hover:text-slate-900'}`}
						>
							{t(language, 'nav.work')}
						</a>
						<div className='flex justify-center gap-4'>
							<ThemeSwitcher />
							<LanguageSwitcher />
						</div>
						<div className='flex gap-6 mt-4'>
							<button
								onClick={() => {
									handleEmailClick()
									toggleMenu()
								}}
								className={`cursor-pointer p-2 rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:text-gray-400 bg-blue-600/20 hover:bg-blue-600/30' : 'text-slate-600 hover:text-slate-900 bg-blue-100 hover:bg-blue-200'}`}
								aria-label={t(language, 'contactModal.sendEmail')}
							>
								<svg
									className='w-6 h-6'
									fill='currentColor'
									viewBox='0 0 20 20'
								>
									<path d='M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z' />
									<path d='M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' />
								</svg>
							</button>
							<button
								onClick={() => {
									handleWhatsAppClick()
									toggleMenu()
								}}
								className={`cursor-pointer p-2 rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:text-gray-400 bg-green-600/20 hover:bg-green-600/30' : 'text-slate-600 hover:text-slate-900 bg-green-100 hover:bg-green-200'}`}
								aria-label={t(language, 'contactModal.chatWhatsApp')}
							>
								<svg
									className='w-6 h-6'
									fill='currentColor'
									viewBox='0 0 24 24'
								>
									<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.223-.298.335-.447.112-.149.056-.279-.008-.391-.064-.111-.298-.272-.298-.272s-.545-.41-.747-.561c-.2-.15-.43-.26-.615-.26-.173 0-.371.074-.57.223-.198.149-.76.74-.76 1.81s.78 2.1.888 2.243c.108.144 1.533 2.457 3.74 3.44.518.231.924.369 1.238.473.518.15 1.001.128 1.377.078.409-.054 1.258-.506 1.436-.992.178-.486.178-.902.124-.992-.053-.089-.198-.149-.446-.248z' />
								</svg>
							</button>
						</div>
					</nav>
				</div>
			</div>
		</>
	)
}

export default Header
